import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SecurityService } from '@features/security';
import { AccountService } from '@features/server-side-authentication';
import * as Sentry from '@sentry/angular';
import { catchError, map, of, tap } from 'rxjs';
import { UserService } from '../services/user/user.service';

export const authGuard: CanActivateFn = () => {
  const router = inject(Router);
  const userService = inject(UserService);
  const securityService = inject(SecurityService);
  const accountService = inject(AccountService);

  return userService.loadUserData().pipe(
    tap((userData) => userService.setUserData(userData)), // userData is the emitted value
    map(() => {
      // if user has no permission ('No Role' role)
      if (securityService.hasNoPermissions()) {
        return router.createUrlTree(['/role-not-assigned']);
      } else {
        return true;
      }
    }),
    catchError((err: HttpErrorResponse) => {
      // Log all non-401 errors to Sentry with additional context
      if (err.status !== HttpStatusCode.Unauthorized) {
        Sentry.withScope((scope) => {
          scope.setTag('guard', 'auth');
          scope.setTag('error_source', 'loadUserData');
          scope.setLevel('error');
          scope.setContext('http_error', {
            status: err.status,
            statusText: err.statusText,
            url: err.url,
            message: err.message,
            error: err.error,
          });
          scope.setContext('user_context', {
            currentUser: userService.user,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
          });

          // Add fingerprint for better error grouping
          scope.setFingerprint(['auth-guard-loadUserData-error', err.status.toString(), err.url || 'unknown-url']);

          Sentry.captureException(new Error(`Auth Guard loadUserData failed: ${err.status} - ${err.statusText}`));
        });
      }

      switch (err.status) {
        case 0:
          return of(router.createUrlTree(['/unknown-error']));
        case HttpStatusCode.Unauthorized:
          accountService.clear();
          return of(router.createUrlTree(['/auth/login']));
        default:
          return of(router.createUrlTree(['/unknown-error'])); //'/un-authenticated'
      }
    }),
  );
};
